import { useEffect, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { prepareModel } from '../utils/modelMaterials';
import { createSoftEnvironment } from '../utils/softEnvironment';
import { locateDroneFace, type FaceSide } from '../utils/droneFace';
import { applyArms, locateArms, type ArmPart, type ArmsMode } from '../utils/droneArms';
import { EMOTION_PARAMS, drawPixelFace, lerpParams } from '../utils/pixelFace';
import { RobotBrain } from '../utils/robotBrain';
import { Spring } from '../utils/spring';
import { PlayRoamer } from '../utils/playRoamer';
import type { EmotionMode, FaceParams } from '../types/emotion';
import type { BubbleLink, ChatSignal, CinemaLink } from '../types/chat';
import { dockBox } from '../utils/dock';
import { ChargeMode } from '../utils/chargeMode';
import { PARTY_DUR, partyPose } from '../utils/partyDance';
import { CHEER_DUR, cheerPose, type CheerPose } from '../utils/questCheer';
import { madness } from '../utils/madness';
import { quest } from '../utils/quest';
import { setInteractionLock } from '../utils/interactionLock';
import { MAD_OFFSET_KEYS, madnessPose, type MadOffsets } from '../utils/madnessPose';
import { PLAY_OFFSET_KEYS, heroPlay, type PlayOffsets } from '../utils/heroPlay';
import { resetUiPush, updateUiPush } from '../utils/uiPush';
import { idleAntics } from '../utils/idleAntics';
import { DroneSwarm, MAX_KIDS } from '../utils/droneSwarm';
import { CHASE_DEPTH, footerChase, type ChaseFrame } from '../utils/footerChase';

export type SceneStatus = 'loading' | 'ready' | 'error';

/**
 * One frame of a screen glitch on the face canvas: three to five horizontal slices torn sideways,
 * a split colour fringe and the odd flicker — clearly a malfunction, but the face stays readable.
 */
function applyFaceGlitch(ctx: CanvasRenderingContext2D, size: number) {
  const slices = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < slices; i++) {
    const h = Math.max(2, Math.round(size * (0.04 + Math.random() * 0.1)));
    const y = Math.floor(Math.random() * (size - h));
    const dx = Math.round((Math.random() - 0.5) * size * 0.24);
    ctx.drawImage(ctx.canvas, 0, y, size, h, dx, y, size, h);
  }
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  // Two colour fringe lines
  for (let k = 0; k < 2; k++) {
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = k === 0 ? '#1A2FFB' : '#FF2D55';
    const ly = Math.floor(Math.random() * size);
    ctx.fillRect(0, ly, size, Math.max(1, Math.round(size * (0.012 + Math.random() * 0.02))));
  }
  // Now and then the whole screen flickers
  if (Math.random() < 0.25) {
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
  }
  ctx.restore();
}

/** Footer chase faces: a furious chaser and a freaked-out runner */
const ANGRY_FACE: FaceParams = { ...EMOTION_PARAMS.angry, lid: 0.4, lidTilt: 0.8, mouthSmile: -1 };
const SCARED_FACE: FaceParams = { ...EMOTION_PARAMS.surprised, lid: 0.05, lidTilt: -0.55, mouthWidth: 0.26, mouthSmile: -0.5 };

export interface RoboticEyeOptions {
  modelUrl: string;
  emotion: EmotionMode;
  screenScale: number;
  faceSide: FaceSide;
  armsMode: ArmsMode;
  /** Conversation state — the drone's body language follows the chat bubbles */
  chat: ChatSignal;
  /** Section the drone floats in; once it's scrolled past, the drone docks bottom-right */
  anchorRef: RefObject<HTMLElement>;
  onDockChange?: (docked: boolean) => void;
  /** He has touched down in the bottom-right corner (true) / is about to leave it (false) */
  onDockLanded?: (landed: boolean) => void;
  /** The drone has finished entering a new spot (e.g. the sneak-in) — false while on the way */
  onArrive?: (arrived: boolean) => void;
  /** Where the chat bubbles attach (drone's upper-right, screen px) */
  bubbleLink: BubbleLink;
  /** Bubbles show only while the drone is settled; they pop away just before it moves */
  onBubblesVisible?: (visible: boolean) => void;
  /** Charge easter egg started / ended — the chat steps aside meanwhile */
  onBusyChange?: (busy: boolean) => void;
  /** Movie-mode overlay (page dim + letterbox) for the full-charge short */
  cinema: CinemaLink;
}

/** Pixel (and his drag kids) shrink to this while dragged */
const DRAG_SCALE = 0.2625;

/** Bubbles pop away this long (s) before a major move starts */
const PRE_MOVE_HIDE = 0.32;
/** …and come back once the drone has been settled this long (s) */
const SETTLE_SHOW = 0.3;
/** Roam target jumps bigger than this (world units) count as a major move */
const MAJOR_MOVE = 0.35;

/** Page-load fly-in starts this long after the page began loading (ms) — after the hero intro */
const DRONE_ARRIVE_AT = 2400;
/** Hero → dock flight — long enough for a soft, decelerating arrival */
const FLIGHT_DUR = 1.35;
/**
 * Flight easing: gentle lift-off, then a long quintic ease-out so the last
 * ~40% of the trip is a slow glide that comes to rest with zero speed.
 */
const flightEase = (p: number) => {
  const g = Math.pow(Math.min(1, Math.max(0, p)), 1.35); // soft start
  return 1 - Math.pow(1 - g, 5);
};
/** How high (world units) the drone arcs up while flying between spots */
const FLIGHT_ARC = 0.9;


/**
 * Screen Y (px) of the pinned hero spot: the hero's centre at the top of the page, sliding
 * down by PINNED_DROP of the viewport as the hero scrolls away. Shared with the hand-over check.
 */
export const PINNED_DROP = 0.15;
export function pinnedCenterY(heroRect: DOMRect, viewportH: number) {
  const docTop = heroRect.top + window.scrollY;
  const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(heroRect.height, 1)));
  const eased = progress * progress * (3 - 2 * progress);
  // Phones: the reel only shrinks in height, symmetric around the screen's middle — so he settles
  // exactly at the vertical centre of the screen (= the centre of the reel)
  const drop = window.innerWidth < 768 ? viewportH / 2 - (docTop + heroRect.height / 2) : PINNED_DROP * viewportH;
  return docTop + heroRect.height / 2 + drop * eased;
}

const smoothstep01 = (v: number) => {
  const k = Math.min(1, Math.max(0, v));
  return k * k * (3 - 2 * k);
};

const SCREEN_SIZE = 256;
const SCREEN_GRID = 26;
const Z_AXIS = new THREE.Vector3(0, 0, 1);
/** Largest model dimension in world units (view height is ~3.5) */
const MODEL_FIT = 1.6;
const MODEL_RADIUS = 0.7;
/** Phones, chat spot: Pixel's size relative to his hero size, and his centre as a share of the screen width */
const MOBILE_SIDE_SCALE = 0.62;
const MOBILE_SIDE_X = 0.3;
/** How quickly a hand-over gap closes (1/s) — ~0.6s to settle */
const HANDOFF_RATE = 5;
/** Phones: how quickly he catches up with his spot while scrolling (1/s) */
const FOLLOW_RATE = 22;
/** Resting spot of the drone in the hero, as normalized screen Y (-1 bottom .. 1 top) */
/** Vertical resting spot: 0 = exact middle of the hero */
const HOME_NDC_Y = 0;
/** How much of the brain's body drift to apply in the hero layout */
const DRIFT_SCALE = 0.6;
/** Point on the drone (local units, before scaling) where the chat bubble's tail attaches: his upper-right */
const CHAT_ATTACH = new THREE.Vector3(0.72, 0.46, 0);

export function useRoboticEyeScene(containerRef: RefObject<HTMLDivElement>, options: RoboticEyeOptions) {
  const [status, setStatus] = useState<SceneStatus>('loading');
  const [progress, setProgress] = useState(0);
  const [armsFound, setArmsFound] = useState<boolean | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionScale = reduceMotion ? 0.3 : 1;
    let width = container.clientWidth;
    let height = container.clientHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    // The canvas covers the whole viewport, so its resolution is the single biggest GPU cost on the page.
    // Start a notch below full retina (antialiasing keeps the edges clean), then adapt: if frames run slow,
    // step the resolution down until the page scrolls smoothly again (never below 1×).
    const MIN_RATIO = 1;
    let pixelRatio = Math.min(window.devicePixelRatio, width < 768 ? 1.25 : 1.5);
    renderer.setPixelRatio(pixelRatio);
    let slowFrames = 0;
    let sampledFrames = 0;
    renderer.setSize(width, height);
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = 1.15;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene(); // transparent — the page shows through

    // Seamless studio dome — even tone on metal parts, no reflected light shapes
    const environment = createSoftEnvironment(renderer);
    scene.environment = environment.texture;
    scene.environmentIntensity = 0.6;

    const camera = new THREE.PerspectiveCamera(30, width / height, 0.1, 100);
    camera.position.set(0, 0, 6.5);

    // Classic 3-point lighting — nothing comes from the camera axis.
    // Key: main light, ~45° to the left and above.
    const key = new THREE.DirectionalLight('#fff4e8', 2.4);
    key.position.set(-4.5, 4, 4);
    // Fill: opposite side, about a third of the key, cooler — lifts the shadows.
    const fill = new THREE.DirectionalLight('#dfe7ff', 0.8);
    fill.position.set(5, 0.5, 3);
    // Rim / back: strong lights behind the drone for a bright separating edge.
    const rim = new THREE.DirectionalLight('#e8f0ff', 3.2);
    rim.position.set(2.5, 3, -6);
    const rimLow = new THREE.DirectionalLight('#e8f0ff', 2);
    rimLow.position.set(-3, -1.5, -6);
    // Very low ambient so the darkest crevices never go fully black.
    const ambient = new THREE.HemisphereLight('#c8ccd6', '#16171b', 0.35);
    const redGlow = new THREE.PointLight('#ff2a1a', 0.6, 3, 2);
    scene.add(key, fill, rim, rimLow, ambient, redGlow);

    const root = new THREE.Group(); // body position + head rotation
    const pivot = new THREE.Group(); // scale + orientation (face toward camera)
    root.add(pivot);
    scene.add(root);

    // Pixel emotion display
    const canvas = document.createElement('canvas');
    canvas.width = SCREEN_SIZE;
    canvas.height = SCREEN_SIZE;
    const ctx = canvas.getContext('2d');
    const screenTexture = new THREE.CanvasTexture(canvas);
    screenTexture.magFilter = THREE.NearestFilter;
    screenTexture.minFilter = THREE.LinearFilter;
    screenTexture.generateMipmaps = false;
    screenTexture.colorSpace = THREE.SRGBColorSpace;
    const screenMaterial = new THREE.MeshBasicMaterial({
      map: screenTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });
    // Faceplate materials: drawn over the model; faded out when the face turns away
    const plateMaterial = new THREE.MeshStandardMaterial({
      color: '#030304',
      roughness: 0.32,
      metalness: 0,
      envMapIntensity: 0.35,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const bezelMaterial = new THREE.MeshStandardMaterial({
      color: '#16171a',
      roughness: 0.45,
      metalness: 0.6,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    let faceGroup: THREE.Group | null = null;
    const faceNormalWorld = new THREE.Vector3();
    const faceWorldPos = new THREE.Vector3();
    const faceWorldQuat = new THREE.Quaternion();
    const screen = new THREE.Mesh(new THREE.CircleGeometry(0.5, 64), screenMaterial);
    screen.renderOrder = 999;
    screen.visible = false;
    pivot.add(screen);

    let ringSize = 1;
    const screenBaseScale = new THREE.Vector3(1, 1, 1);
    let ledMaterials: ReturnType<typeof prepareModel>['ledMaterials'] = [];
    let faceDir = new THREE.Vector3(0, 0, 1);
    let mixer: THREE.AnimationMixer | null = null;
    let modelReady = false;
    let disposed = false;

    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    loader.load(
      optionsRef.current.modelUrl,
      (gltf) => {
        if (disposed) return;
        const model = gltf.scene;
        const prepared = prepareModel(model);
        ledMaterials = prepared.ledMaterials;

        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const scale = MODEL_FIT / Math.max(size.x, size.y, size.z);

        // Side plates that can tuck in to form a ball
        armParts = locateArms(model, center, size);
        setArmsFound(armParts.length > 0);

        // Find the drone's circular face (dark disc with the three red dots)
        const face = locateDroneFace(model, center, size, optionsRef.current.faceSide);
        faceDir = face.normal;
        ringSize = face.plateDiameter;
        face.dots.forEach((dot) => {
          dot.visible = false;
        });

        // New faceplate: a black screen with a thin bezel, floating just in front of the old face.
        // Built as one group attached to the model so it moves and rotates with it.
        faceGroup = new THREE.Group();
        faceGroup.position.copy(face.center).addScaledVector(face.normal, face.depth);
        faceGroup.quaternion.setFromUnitVectors(Z_AXIS, face.normal);

        // The plate is drawn on top of the model (no depth test) so the red dots and
        // anything else inside the face can never poke through the pixel display.
        const plate = new THREE.Mesh(new THREE.CircleGeometry(0.5, 96), plateMaterial);
        plate.scale.setScalar(face.plateDiameter);
        plate.renderOrder = 997;
        const bezel = new THREE.Mesh(new THREE.RingGeometry(0.5, 0.545, 96), bezelMaterial);
        bezel.scale.setScalar(face.plateDiameter);
        bezel.renderOrder = 998;

        screen.position.set(0, 0, face.plateDiameter * 0.006);
        screen.quaternion.identity();
        faceGroup.add(plate, bezel, screen);
        model.add(faceGroup);
        screenBaseScale.set(1, 1, 1);

        // Rotate around the face center so the face sits dead-center on screen
        const lensOffset = face.center.clone().sub(center);
        model.position.copy(center).add(lensOffset.projectOnPlane(faceDir)).negate();
        pivot.add(model);
        pivot.scale.setScalar(scale);
        pivot.quaternion.setFromUnitVectors(faceDir, Z_AXIS);
        screen.visible = true;

        if (gltf.animations.length) {
          mixer = new THREE.AnimationMixer(model);
          gltf.animations.forEach((clip) => mixer?.clipAction(clip).play());
        }
        // Fly-in: arrive from below-left curled into a ball, spinning, then unfold at home
        if (!reduceMotion) {
          // Waits off-stage until the hero intro has mostly resolved, then flies in
          const launch = () => {
            if (disposed) return;
            const v = viewHalf();
            // Offsets from the resting spot
            bodyX.value = -Math.min(v.w * 0.6, 2.2);
            bodyY.value = -v.h - 1.4;
            bodyX.velocity = 1.2;
            bodyY.velocity = 5.5;
            spinYaw.value = 1.6;
            spinPitch.value = -0.25;
            arms.value = 1;
            lastArmsT = -1;
            introUntil = clock.elapsedTime + 1.1;
            pivot.visible = true;
          };
          const wait = Math.max(0, DRONE_ARRIVE_AT - performance.now());
          if (wait > 0) {
            pivot.visible = false;
            window.setTimeout(launch, wait);
          } else {
            launch();
          }
        }
        modelReady = true;
        setProgress(1);
        setStatus('ready');
      },
      (event) => {
        if (event.lengthComputable) setProgress(event.loaded / event.total);
      },
      () => {
        if (!disposed) setStatus('error');
      },
    );

    const clock = new THREE.Clock();
    const brain = new RobotBrain();
    const charge = new ChargeMode();
    /** Quest take-off charge: the very same battery screen, opened by the quest, a bit longer to fill */
    const questCharge = new ChargeMode({ seconds: 4, sticky: true });
    let questChargeReported = false;

    // --- Input ---
    let scrollTarget = 0;
    let scrollSmooth = 0;
    let scrollVelocity = 0;
    /** Signed scroll impulse (down = positive) — drives the docked drone's wobble */
    let scrollSigned = 0;
    let lastScrollY = window.scrollY;
    const pointer = { x: 0, y: 0 };
    let pointerMovedAt = -10;
    let pointerInWindow = true;
    let pointerSpeedRaw = 0;
    let pointerSpeed = 0;
    let lastPointerTime = performance.now();
    let hovering = false;

    // --- Drag to rotate ---
    // Resting spots (world units) + drone scale: middle of the hero, or docked bottom-right
    const heroAnchor = { x: 0, y: 0, scale: 1 };
    const dockAnchor = { x: 0, y: 0, scale: 0.3 };
    let heroBottom = Infinity;
    /** Section end must rise above this fraction of the screen for him to dock (per spot) */
    let dockAtFrac = 0.74;
    // Page scroll speed (px/s, + = down, smoothed) — used to dock early on fast scrolls
    let dockScrollY = window.scrollY;
    let dockScrollVel = 0;
    /** Drone scale in the pinned hero spot — reused by compact section spots */
    let fixedScale = 0;
    /** Hand-over glide: offset from the new spot that eases to zero (see HANDOFF_RATE) */
    const handoff = { x: 0, y: 0 };
    let handoffPending = false;
    let lastBaseX = 0;
    let lastBaseY = 0;
    let lastBaseValid = false;
    // Phones: the spot's raw screen position arrives in uneven steps while touch-scrolling (the page
    // scrolls on its own thread), so he follows it through a light, frame-rate independent smoothing
    const follow = { x: 0, y: 0, valid: false };
    /** Where the drone actually is (switches when the flight starts) */
    let docked = false;
    /** Where it's headed — reported to the UI right away so bubbles can pop away first */
    let wantDock = false;
    let dockSwitchAt = -1;
    // Major-move gating for the chat bubbles
    let acceptedRoam = 0;
    let roamPendingSince = -1;
    let settledSince = -1;
    let bubblesVisible = false;
    let lastChargeOn = false;
    const madProj = new THREE.Vector3();
    const madP: MadOffsets = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, led: 0, shake: 0, squint: 0 };
    // Hero play behaviours (desktop): board / interest / investigate / focus / dizzy / hypno
    const playP: PlayOffsets = {
      x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, squint: 0, led: 0, board: 0, wide: 0, spiral: 0, tight: 0, scan: 0,
    };
    let playHeading = 0;
    let playSpin = 0;
    const headingQ = new THREE.Quaternion();
    const pushProj = new THREE.Vector3();
    const pushEdge = new THREE.Vector3();
    const desktopMq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const ptrPx = { x: -9999, y: -9999 };
    // Drag Pixel (desktop, hero): he shrinks, follows the cursor, bumps UI aside — keep going and his
    // copies fly in and trail behind him
    const swarm = new DroneSwarm();
    /** Face glitch bursts (quest): when the next starts, when the current one ends (scene time, s) */
    let nextGlitch = 0;
    let glitchUntil = 0;
    let dragPending: { x: number; y: number; t: number } | null = null;
    let dragAllowedNow = false;
    let dragScale = 1;
    const dragOff = { x: 0, y: 0 };
    const kidRoots: THREE.Group[] = [];
    const kidProj = new THREE.Vector3();
    const ensureKids = () => {
      if (kidRoots.length || !modelReady) return;
      for (let i = 0; i < MAX_KIDS; i++) {
        const g = new THREE.Group();
        // Skeleton-aware clone: a plain clone() leaves skinned meshes bound to the original's bones,
        // which renders the copies invisible / collapsed
        const copy = cloneSkinned(pivot);
        copy.visible = true;
        // Pixel's faceplate skips the depth test (drawn over his own body). Shared with the cousins,
        // that made their faces render on top of Pixel. Give each cousin depth-aware copies, drawn
        // before Pixel's plate, so Pixel — face and all — always stays in front.
        const depthAware = <M extends THREE.Material>(src: M): M => {
          const m = src.clone() as M;
          m.depthTest = true;
          m.depthWrite = false;
          m.polygonOffset = true;
          m.polygonOffsetFactor = -2;
          m.polygonOffsetUnits = -2;
          return m;
        };
        const faceMat = depthAware(screenMaterial);
        const plateMat = depthAware(plateMaterial);
        const bezelMat = depthAware(bezelMaterial);
        copy.traverse((o) => {
          o.visible = true;
          o.frustumCulled = false;
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          if (m.material === screenMaterial) {
            m.material = faceMat;
            m.renderOrder = 992;
          } else if (m.material === plateMaterial) {
            m.material = plateMat;
            m.renderOrder = 990;
          } else if (m.material === bezelMaterial) {
            m.material = bezelMat;
            m.renderOrder = 991;
          }
        });
        g.add(copy);
        g.visible = false;
        scene.add(g);
        kidRoots.push(g);
      }
    };
    // Footer chase: two tiny bots far in the background (lazy clones of Pixel, like the drag kids)
    const visitorRoots: THREE.Group[] = [];
    // Each visitor gets its own face display (the clones would otherwise mirror Pixel's face)
    const visitorFaces: Array<{ ctx: CanvasRenderingContext2D | null; tex: THREE.CanvasTexture }> = [];
    const ensureVisitors = () => {
      if (visitorRoots.length || !modelReady) return;
      for (let i = 0; i < 2; i++) {
        const g = new THREE.Group();
        const copy = cloneSkinned(pivot);
        copy.visible = true;
        const faceCanvas = document.createElement('canvas');
        faceCanvas.width = SCREEN_SIZE;
        faceCanvas.height = SCREEN_SIZE;
        const tex = new THREE.CanvasTexture(faceCanvas);
        tex.magFilter = THREE.NearestFilter;
        tex.minFilter = THREE.LinearFilter;
        tex.generateMipmaps = false;
        tex.colorSpace = THREE.SRGBColorSpace;
        // Pixel's own faceplate skips the depth test (drawn over his body). The visitors fly *behind* him,
        // so their faceplate must respect depth — otherwise their red face pixels drew on top of Pixel.
        // A small polygon offset keeps the plate from flickering against their own body.
        const depthAware = <M extends THREE.Material>(src: M): M => {
          const m = src.clone() as M;
          m.depthTest = true;
          m.depthWrite = false;
          m.polygonOffset = true;
          m.polygonOffsetFactor = -2;
          m.polygonOffsetUnits = -2;
          return m;
        };
        const faceMat = depthAware(screenMaterial);
        faceMat.map = tex;
        faceMat.opacity = 1;
        const plateMat = depthAware(plateMaterial);
        const bezelMat = depthAware(bezelMaterial);
        visitorFaces.push({ ctx: faceCanvas.getContext('2d'), tex });
        copy.traverse((o) => {
          o.visible = true;
          o.frustumCulled = false;
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          // Drawn before Pixel's faceplate (997–999), so his face always stays on top
          if (m.material === screenMaterial) {
            m.material = faceMat;
            m.renderOrder = 992;
          } else if (m.material === plateMaterial) {
            m.material = plateMat;
            m.renderOrder = 990;
          } else if (m.material === bezelMaterial) {
            m.material = bezelMat;
            m.renderOrder = 991;
          }
        });
        g.add(copy);
        g.visible = false;
        scene.add(g);
        visitorRoots.push(g);
      }
    };
    const startDrag = (t: number) => {
      dragPending = null;
      ensureKids();
      swarm.begin(t);
      brain.express('surprised', 0.5, t);
      document.body.style.userSelect = 'none';
    };
    const endDrag = (t: number) => {
      dragPending = null;
      if (!swarm.dragging) return;
      swarm.end();
      brain.express('happy', 1.2, t);
    };
    const floatNow = new THREE.Vector3();
    const floatHome = new THREE.Vector3();
    let floatX = 0;
    let floatY = 0;
    // Velocities for the chat's smooth (critically damped) follow
    let floatVX = 0;
    let floatVY = 0;
    const bubbleWorld = new THREE.Vector3();
    let bubbleX = -9999;
    let bubbleY = -9999;
    let dockW = 0; // smoothed 0 (hero) .. 1 (docked)
    let curScale = 1;
    let flight: { start: number; from: { x: number; y: number; scale: number } } | null = null;
    // Which element the drone currently lives in; switching it moves the drone there
    let anchorEl: HTMLElement | null = null;
    let lastBlinkTag: string | undefined;
    /** Only section spots marked data-drone-dock fly to the corner when scrolled past */
    let dockAllowed = false;
    let arrived = false;
    const setArrived = (a: boolean) => {
      if (a === arrived) return;
      arrived = a;
      optionsRef.current.onArrive?.(a);
    };
    const NO_DRAG = { x: 0, y: 0 };
    const projCenter = new THREE.Vector3();
    const projEdge = new THREE.Vector3();
    const radiusOffset = new THREE.Vector3(0, MODEL_RADIUS, 0);

    const viewHalf = () => {
      const h = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.position.z;
      return { w: h * camera.aspect, h };
    };
    const updateAnchors = () => {
      const v = viewHalf();
      const el = optionsRef.current.anchorRef.current;
      if (el) {
        // Middle of the hero as it scrolls; scaled so the drone keeps its hero size
        const r = el.getBoundingClientRect();
        // Pinned spots (hero) stay where they sit at the top of the page, whatever the scroll
        // …easing ~15% of the screen lower as the hero scrolls away, where the eye naturally rests
        const centerY = el.dataset.droneFixed === 'true' ? pinnedCenterY(r, height) : r.top + r.height / 2;
        heroAnchor.x = (((r.left + r.width / 2) / width) * 2 - 1) * v.w;
        heroAnchor.y = (-((centerY / height) * 2 - 1) + HOME_NDC_Y) * v.h;
        // Spots marked data-drone-keep-scale reuse the pinned hero size, so compact spots don't shrink him
        // Spots marked data-drone-free-scale may make him bigger than his hero size (flying up to the camera)
        if (el.dataset.droneFreeScale === 'true') heroAnchor.scale = r.height / Math.max(height, 1);
        else if (el.dataset.droneKeepScale === 'true' && fixedScale > 0) heroAnchor.scale = fixedScale;
        else heroAnchor.scale = Math.min(1, r.height / Math.max(height, 1));
        // Phones: the hero now fills the full screen, but Pixel keeps the size he had when it was
        // 80vh tall (min 600px) — his original mobile size
        if (el.dataset.droneFixed === 'true' && width < 768) {
          heroAnchor.scale = Math.min(heroAnchor.scale, Math.min(1, Math.max(height * 0.8, 600) / Math.max(height, 1)));
        }
        if (el.dataset.droneFixed === 'true') fixedScale = heroAnchor.scale;
        // Phones, chat spots marked data-drone-mobile-left: a bit smaller and over on the left,
        // leaving the right side free for his chat bubbles
        if (el.dataset.droneMobileLeft === 'true' && width < 768) {
          heroAnchor.scale *= MOBILE_SIDE_SCALE;
          heroAnchor.x = (MOBILE_SIDE_X * 2 - 1) * v.w;
        }
        // Phones: spots may shrink him further with data-drone-mobile-scale (e.g. the compact footer)
        const mScale = parseFloat(el.dataset.droneMobileScale ?? '');
        if (width < 768 && Number.isFinite(mScale)) heroAnchor.scale *= mScale;
        // Published so a scripted flight can start from exactly the size he has here (no jump)
        el.dataset.droneScaleUsed = heroAnchor.scale.toFixed(4);
        // Dock once the whole section holding the spot is mostly scrolled past
        heroBottom = (el.closest('section') ?? el).getBoundingClientRect().bottom;
        // Spots may dock earlier/later: data-drone-dock-at = fraction of the screen height the section end must rise to
        // Phones may override it with data-drone-dock-at-mobile
        const mobileAt = width < 768 ? el.dataset.droneDockAtMobile : undefined;
        const at = parseFloat(mobileAt ?? el.dataset.droneDockAt ?? '');
        dockAtFrac = Number.isFinite(at) ? at : 0.74;
      }
      // Bottom-right corner, sized like a chat launcher
      const { size, margin } = dockBox(width);
      const c = margin + size / 2;
      dockAnchor.x = (((width - c) / width) * 2 - 1) * v.w;
      // The canvas spans the large viewport on phones; the corner follows the *visible* bottom edge
      // (above the address bar when it's showing), like the corner chat does
      const visibleH = Math.min(height, window.innerHeight || height);
      dockAnchor.y = -(((visibleH - c) / height) * 2 - 1) * v.h;
      dockAnchor.scale = (size * 2 * v.h) / (MODEL_FIT * 1.15 * Math.max(height, 1));
    };
    const startFlight = (t: number) => {
      flight = { start: t, from: { x: root.position.x, y: root.position.y, scale: curScale } };
      roam.value = 0;
      roam.velocity = 0;
      brain.express(docked ? 'happy' : 'surprised', 0.5, t);
    };
    // Pointer position in the canvas' own normalized space
    const toLocal = (clientX: number, clientY: number) => {
      const rect = container.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1,
        y: -(((clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1),
      };
    };
    const hitTest = (nx: number, ny: number) => {
      if (!modelReady) return false;
      projCenter.copy(root.position).project(camera);
      projEdge.copy(root.position).addScaledVector(radiusOffset, root.scale.y).project(camera);
      const radius = Math.abs(projEdge.y - projCenter.y);
      const aspect = width / height;
      return Math.hypot((nx - projCenter.x) * aspect, ny - projCenter.y) < radius;
    };

    const readScroll = () => {
      // The drone simply rides along with its section — no scroll-driven lift or turn
      scrollTarget = 0;
      scrollVelocity += Math.abs(window.scrollY - lastScrollY) / window.innerHeight;
      scrollSigned += (window.scrollY - lastScrollY) / window.innerHeight;
      lastScrollY = window.scrollY;
    };
    const onPointerMove = (e: PointerEvent) => {
      const { x: nx, y: ny } = toLocal(e.clientX, e.clientY);
      const now = performance.now();
      const elapsed = Math.max((now - lastPointerTime) / 1000, 0.008);
      pointerSpeedRaw = Math.max(pointerSpeedRaw, Math.hypot(nx - pointer.x, ny - pointer.y) / 2 / elapsed);
      lastPointerTime = now;
      pointer.x = nx;
      pointer.y = ny;
      ptrPx.x = e.clientX;
      ptrPx.y = e.clientY;
      pointerMovedAt = clock.elapsedTime;
      pointerInWindow = true;
      if (dragPending && Math.hypot(e.clientX - dragPending.x, e.clientY - dragPending.y) > 6) {
        startDrag(clock.elapsedTime);
      }
    };
    // Is the pointer on the face display itself (the middle of Pixel)?
    const faceProj = new THREE.Vector3();
    const faceEdgeProj = new THREE.Vector3();
    const hitFace = (nx: number, ny: number) => {
      if (!modelReady || !faceGroup || !faceGroup.visible || plateMaterial.opacity < 0.5) return false;
      screen.getWorldPosition(faceProj).project(camera);
      faceEdgeProj.set(0.5, 0, 0);
      screen.localToWorld(faceEdgeProj).project(camera);
      const aspect = width / height;
      const radius = Math.hypot((faceEdgeProj.x - faceProj.x) * aspect, faceEdgeProj.y - faceProj.y);
      return Math.hypot((nx - faceProj.x) * aspect, ny - faceProj.y) < radius;
    };

    // Tapping Pixel = surprised + happy hop; tapping elsewhere = he looks there.
    // Triple-tapping his face opens the charge screen; while charging every tap pumps the battery.
    const onPointerDown = (e: PointerEvent) => {
      onPointerMove(e);
      const t = clock.elapsedTime;
      const onBody = hitTest(pointer.x, pointer.y);
      // Quest restoration: touching Pixel is the only control — none of his usual tricks
      // (charge screen, drag, hero play, click hop) may fire; he holds still so you can aim
      if (quest.takesPixelInput()) {
        // Take-off charge: every click on him pumps the battery, exactly like the battery easter egg
        if (quest.wantsBattery() && questCharge.click(t, true, onBody)) ledKick = Math.max(ledKick, 0.8);
        quest.finaleInput?.(true, onBody);
        return;
      }
      // Hero play: hold to drop him onto the "table", rapid clicks to make him investigate
      if (e.pointerType === 'mouse' && e.button === 0) {
        const interactive =
          e.target instanceof Element && !!e.target.closest('a,button,input,textarea,select,label,[role="button"]');
        heroPlay.pointerDown(e.clientX, e.clientY, t, onBody, interactive);
        // No accidental text selection while holding
        if (heroPlay.holding) document.body.style.userSelect = 'none';
        // Pressing on Pixel himself arms a drag (starts on movement or a short hold)
        if (onBody && dragAllowedNow) dragPending = { x: e.clientX, y: e.clientY, t };
      }
      if (!docked && charge.click(t, onBody && hitFace(pointer.x, pointer.y), onBody)) {
        // Each impulse flashes the lights and buzzes in place — no upward jolt
        if (charge.state !== 'party') ledKick = Math.max(ledKick, 0.8);
        return;
      }
      brain.click(onBody, t, pointer);
    };
    const onPointerUp = () => {
      if (quest.takesPixelInput()) quest.finaleInput?.(false, false);
      heroPlay.pointerUp();
      endDrag(clock.elapsedTime);
      document.body.style.userSelect = '';
    };
    const onLeave = () => {
      pointerInWindow = false;
      onPointerUp();
    };
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      // Phones fire resize as the address bar slides in/out mid-scroll. The canvas is sized to the
      // large viewport so its size doesn't change then — only re-size (which clears and re-scales
      // the scene, causing a visible jump) when the dimensions really changed
      if (w !== width || h !== height) {
        width = w;
        height = h;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
      updateAnchors();
    };

    updateAnchors();
    curScale = heroAnchor.scale;
    readScroll();
    scrollSmooth = scrollTarget;
    scrollVelocity = 0;
    window.addEventListener('scroll', readScroll, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    idleAntics.attach();
    window.addEventListener('pointercancel', onPointerUp, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    window.addEventListener('blur', onLeave);
    window.addEventListener('resize', onResize);

    // --- Motion state ---
    const params: FaceParams = { ...EMOTION_PARAMS.neutral };
    const look = { x: 0, y: 0 };
    let nextBlink = 2.5;
    let blinkStart = -1;
    let doubleBlink = false;

    // Head responds quickly with a little overshoot; body drifts slowly
    const yaw = new Spring(38, 8.5);
    const pitch = new Spring(38, 8.5);
    const roll = new Spring(26, 6.5);
    const bodyX = new Spring(7, 4);
    const bodyY = new Spring(7, 4);
    const bodyZ = new Spring(10, 5);
    // Side plates: a quick, slightly bouncy "clack" when they lock against the body
    const arms = new Spring(22, 6);
    let armParts: ArmPart[] = [];
    let lastArmsT = 0;
    /** Until this time the drone stays curled up while flying in */
    let introUntil = 0;
    // Playful roaming left/right across the hero
    const roamer = new PlayRoamer();
    const roam = new Spring(14, 5.2); // lively with a little overshoot when it stops
    let lastEmotion: string = 'neutral';
    // Chat sync: each new bubble = hop + LED flash; mouth moves while text writes out
    let lastPulse = 0;
    let ledKick = 0;
    let talk = 0;
    // Drag-to-rotate: low stiffness so a flick spins freely before easing back
    const spinYaw = new Spring(9, 3.2);
    const spinPitch = new Spring(12, 4.5);
    let bobPhase = 0;
    let bobAmp = 0.08;

    const glowWorld = new THREE.Vector3();
    const glowOffset = new THREE.Vector3(0, 0, 0.5);

    let frame = 0;

    const updateHover = () => {
      hovering = pointerInWindow && hitTest(pointer.x, pointer.y);
      // Grab hand while dragging Pixel, open hand over him when he can be grabbed
      document.body.style.cursor =
        swarm.dragging
          ? 'grabbing'
          : hovering && quest.takesPixelInput()
            ? 'pointer'
            : hovering && dragAllowedNow
              ? 'grab'
              : '';
    };

    const tick = () => {
      frame = requestAnimationFrame(tick);
      const rawDt = clock.getDelta();
      const dt = Math.min(rawDt, 0.05);
      const t = clock.elapsedTime;

      // Adaptive resolution: over each ~90-frame window, if more than a third ran slower than ~45fps,
      // render at a lower resolution (ignores one-off hitches like tab switches)
      if (pixelRatio > MIN_RATIO && rawDt < 0.25) {
        sampledFrames++;
        if (rawDt > 1 / 45) slowFrames++;
        if (sampledFrames >= 90) {
          if (slowFrames > 30) {
            pixelRatio = Math.max(MIN_RATIO, pixelRatio - 0.25);
            renderer.setPixelRatio(pixelRatio);
            renderer.setSize(width, height);
          }
          sampledFrames = 0;
          slowFrames = 0;
        }
      }
      const opts = optionsRef.current;

      // Inputs
      scrollVelocity *= Math.exp(-dt * 4);
      pointerSpeed += (pointerSpeedRaw - pointerSpeed) * (1 - Math.exp(-dt * 10));
      pointerSpeedRaw *= Math.exp(-dt * 8);
      updateHover();

      // Hero ↔ dock: flies to the bottom-right once the hero is mostly scrolled away, back when it returns
      updateAnchors();

      // New home element (hero → section spot, or back): sneak in from the top, or fly back
      const el = opts.anchorRef.current;
      if (el !== anchorEl) {
        const first = anchorEl === null;
        // Spots marked data-drone-jump don't line up with their neighbours, so he hops there (and back) in an arc
        const jumpSpot = anchorEl?.dataset.droneJump === 'true' || el?.dataset.droneJump === 'true';
        anchorEl = el;
        if (!first && !reduceMotion) handoffPending = true;
        dockAllowed = el?.dataset.droneDock === 'true';
        setArrived(false);
        const animate = !first && modelReady && !reduceMotion;
        // Hero ↔ section spot hand-over happens exactly where the two spots line up on screen,
        // so it's seamless — he just stops being pinned and rides along with the section
        if (animate && !docked && dockAllowed) brain.express('curious', 1.6, t);
        if (dockAllowed) setArrived(true);
        // New home whose section is already scrolled past (e.g. scrolling back up from the finale spot to the
        // manifesto spot far above): go straight to the corner from where he is now — no pre-flight pause,
        // otherwise he'd first chase the off-screen spot and get flung off the top of the screen
        if (!first && dockAllowed && !docked && heroBottom < height * dockAtFrac) {
          wantDock = true;
          opts.onDockChange?.(true);
          dockSwitchAt = -1;
          docked = true;
          if (modelReady && !reduceMotion) startFlight(t);
          else {
            curScale = dockAnchor.scale;
            opts.onDockLanded?.(true);
          }
        }
        // Jump to / from a jump spot: same arc as the dock flight; he "arrives" once he lands
        if (jumpSpot && animate && !docked) {
          startFlight(t);
          setArrived(false);
        }
      }

      // Leaves for the corner once the section's end has scrolled up to ~62% of the screen
      // (comes back only once it's below ~78%, so it never flickers at the edge)
      const sy = window.scrollY;
      if (dt > 0) {
        const inst = (sy - dockScrollY) / dt;
        dockScrollVel += (inst - dockScrollVel) * (1 - Math.exp(-dt * 12));
      }
      dockScrollY = sy;
      // Fast scrolling: look ahead to where the section will be once he's airborne, and skip the
      // pre-flight pause — otherwise the page outruns him and he leaves noticeably late
      const fastDown = dockScrollVel > 900;
      const lookahead = Math.max(0, dockScrollVel) * (PRE_MOVE_HIDE + 0.25);
      const dockEdge = wantDock ? heroBottom : heroBottom - Math.min(lookahead, height * 0.6);
      const shouldDock = dockAllowed && (wantDock ? dockEdge < height * (dockAtFrac + 0.15) : dockEdge < height * dockAtFrac);
      if (shouldDock !== wantDock) {
        // Tell the UI first (bubbles pop away), take off a beat later
        wantDock = shouldDock;
        opts.onDockChange?.(wantDock);
        // Leaving the corner: corner UI pops away right now, before take-off
        if (!wantDock) opts.onDockLanded?.(false);
        // Changed its mind before taking off (e.g. a fast guided scroll flickering past the edge):
        // he never left the corner, so his corner chat must come straight back — otherwise it
        // stayed switched off until he flew out and back in
        if (wantDock && docked && !flight) {
          dockSwitchAt = -1;
          opts.onDockLanded?.(true);
        } else {
          const pause = wantDock && fastDown ? 0.05 : PRE_MOVE_HIDE;
          dockSwitchAt = t + (modelReady && !reduceMotion ? pause : 0);
        }
      }
      if (dockSwitchAt >= 0 && t >= dockSwitchAt) {
        dockSwitchAt = -1;
        if (docked !== wantDock) {
          docked = wantDock;
          if (modelReady && !reduceMotion) startFlight(t);
          else {
            curScale = (docked ? dockAnchor : heroAnchor).scale;
            if (docked) opts.onDockLanded?.(true);
          }
        } else if (docked && !flight) {
          // Still sitting in the corner: make sure his chat is switched back on
          opts.onDockLanded?.(true);
        }
      }
      // During a flight the hero↔dock blend follows the flight curve exactly, so every
      // hero-only / dock-only offset fades out in step and nothing is left to settle after touchdown
      if (flight) {
        const fe = flightEase(Math.min(1, (t - flight.start) / FLIGHT_DUR));
        dockW = docked ? Math.max(dockW, fe) : Math.min(dockW, 1 - fe);
      } else {
        dockW += ((docked ? 1 : 0) - dockW) * (1 - Math.exp(-dt * 5));
      }
      const heroW = 1 - dockW;
      scrollSigned = THREE.MathUtils.clamp(scrollSigned * Math.exp(-dt * 3), -0.6, 0.6);
      const target = docked ? dockAnchor : heroAnchor;
      // Small and calm in the corner: drift, bob and hops shrink with it
      const driftScale = THREE.MathUtils.lerp(1, 0.5 * curScale, dockW);

      // Charge easter egg (hero only)
      if (docked && charge.state !== 'off') charge.reset(t);
      charge.update(t, dt);
      const chargeOn = charge.state !== 'off';
      if (chargeOn !== lastChargeOn) {
        lastChargeOn = chargeOn;
        opts.onBusyChange?.(chargeOn);
      }
      const party = charge.state === 'party' && !reduceMotion ? partyPose(t - charge.stateAt) : null;

      // Quest take-off charge: battery screen up while the quest asks for it; full → lift-off
      const questBattery = quest.wantsBattery();
      if (questBattery && questCharge.state === 'off') {
        questCharge.begin(t);
        questChargeReported = false;
      } else if (!questBattery && questCharge.state !== 'off') {
        questCharge.reset(t);
      }
      questCharge.update(t, dt);
      if (questCharge.state === 'party' && !questChargeReported) {
        questChargeReported = true;
        quest.batteryFull();
      }
      const questChargeOn = questBattery && questCharge.state !== 'off';
      quest.batteryLevel = questChargeOn ? questCharge.level : 0;

      // Quest celebrations: a choreographed move on the spot (flips, spins, rolls)
      let cheer: CheerPose | null = null;
      if (quest.cheerNow && !reduceMotion) {
        const ct = (performance.now() - quest.cheerNow.at) / 1000;
        if (ct <= CHEER_DUR[quest.cheerNow.kind]) cheer = cheerPose(quest.cheerNow.kind, ct);
        else quest.cheerNow = null;
      }
      // Nothing else on the site can be scrolled or clicked while the short plays
      setInteractionLock(!!party);

      // Madness (CONTACT teasing): excited → annoyed → psychic crash into the screen
      const madS = madness.sample(performance.now());
      // Mad glare + crash only: lift the whole drone layer above the showreel (and everything else
      // except the glass cracks) so the close-up and the hit are never hidden behind the video
      const madLayer = madS.stage === 'mad' || madS.stage === 'psychic';
      const layerEl = container.parentElement;
      if (layerEl) {
        // Quest mode too: he travels with you over every section (reel included), never behind it
        const z = madLayer || quest.isActive() ? '65' : '';
        if (layerEl.style.zIndex !== z) layerEl.style.zIndex = z;
      }
      const madOn = !docked && !flight && !party && modelReady;
      const mad =
        madOn && !reduceMotion
          ? madnessPose(madS, t, pointer, { x: target.x + roam.value * heroW, y: target.y })
          : null;
      // Ease every offset toward its target so stage changes blend instead of snapping
      // (snappier during the crash so the rush still hits hard)
      // (snappy in the mad glare so the head snaps read, slow and floaty while dizzy)
      const madRate = madS.stage === 'psychic' ? 14 : madS.stage === 'mad' ? 6 : madS.stage === 'dizzy' ? 2.5 : 3.2;
      // The rush at the glass and the bounce follow the keyframes exactly (no easing lag), so he
      // really touches the glass on the very frame the crack appears
      const rushing = madS.stage === 'psychic' && madS.crashT >= 1.45 && madS.crashT < 2.45;
      const madK = rushing ? 1 : 1 - Math.exp(-dt * madRate);
      for (const k of MAD_OFFSET_KEYS) madP[k] += ((mad ? mad[k] : 0) - madP[k]) * madK;
      const crackNow = madness.needsImpact(madS.crashT);

      // Hero play (desktop only, hero spot only, nothing else going on)
      const playEnabled =
        desktopMq.matches &&
        width >= 1024 &&
        !reduceMotion &&
        modelReady &&
        !docked &&
        !flight &&
        !dockAllowed &&
        !party &&
        !chargeOn &&
        madS.stage === 'calm' &&
        t > introUntil &&
        // Scrolling out of the hero ends it; deep into the reel everything resets
        window.scrollY < height * 0.55;
      // Dragging Pixel uses the same conditions
      dragAllowedNow = playEnabled && !heroPlay.active;
      if (!playEnabled && (swarm.dragging || dragPending)) endDrag(t);
      if (dragPending && t - dragPending.t > 0.35) startDrag(t);
      const dragging = swarm.dragging;
      let facePx: { x: number; y: number; r: number } | null = null;
      if (playEnabled && faceGroup && faceGroup.visible && plateMaterial.opacity > 0.5) {
        screen.getWorldPosition(faceProj).project(camera);
        faceEdgeProj.set(0.5, 0, 0);
        screen.localToWorld(faceEdgeProj).project(camera);
        const fxp = ((faceProj.x + 1) / 2) * width;
        const fyp = ((1 - faceProj.y) / 2) * height;
        facePx = {
          x: fxp,
          y: fyp,
          r: Math.hypot(((faceEdgeProj.x + 1) / 2) * width - fxp, ((1 - faceEdgeProj.y) / 2) * height - fyp),
        };
      }
      pushProj.copy(root.position).project(camera);
      pushEdge.copy(root.position).addScaledVector(radiusOffset, root.scale.y).project(camera);
      const dronePx = { x: ((pushProj.x + 1) / 2) * width, y: ((1 - pushProj.y) / 2) * height };
      const droneRPx = (Math.abs(pushEdge.y - pushProj.y) / 2) * height;
      // Quest: publish where he actually is on screen (viewport px), so his restoration chat sticks to him
      if (quest.isActive()) {
        const cr = container.getBoundingClientRect();
        quest.pixelPx.x = cr.left + dronePx.x;
        quest.pixelPx.y = cr.top + dronePx.y;
        quest.pixelPx.r = droneRPx;
      }
      const heroRect = (anchorEl?.closest('section') ?? anchorEl)?.getBoundingClientRect();
      const heroPose = heroPlay.update({
        t,
        dt,
        enabled: playEnabled && !dragging,
        pointer: ptrPx,
        pointerInWindow,
        face: facePx,
        drone: dronePx,
        droneR: droneRPx,
        home: { x: target.x + roam.value * heroW, y: target.y },
        view: viewHalf(),
        width,
        height,
        heroTop: heroRect ? heroRect.top : 0,
        heroBottom: heroRect ? heroRect.bottom : height,
      });
      // Idle antics (any device): bored after a while without input → he entertains himself
      const idleOn =
        !heroPose &&
        !dragging &&
        !reduceMotion &&
        modelReady &&
        !docked &&
        !flight &&
        !dockAllowed &&
        !party &&
        !chargeOn &&
        madS.stage === 'calm' &&
        t > introUntil + 1 &&
        window.scrollY < height * 0.55;
      const idlePose = idleAntics.update(t, idleOn);
      // Footer chase: once he's settled in the footer for a while, two bots chase each other behind him
      const chaseOn =
        !heroPose &&
        !dragging &&
        !reduceMotion &&
        modelReady &&
        !docked &&
        !flight &&
        !party &&
        !chargeOn &&
        madS.stage === 'calm' &&
        anchorEl?.dataset.droneFooter === 'true';
      const chase: ChaseFrame | null = footerChase.update(t, chaseOn);
      if (chase) {
        ensureVisitors();
        // Eyes + head follow the watched bot (its u/v are already screen NDC), measured from where he sits
        const rest = chase.pose.gaze ?? { x: 0, y: 0 };
        if (chase.watch !== null && chase.watchK > 0) {
          const vis = chase.visitors[chase.watch];
          kidProj.copy(root.position).project(camera);
          const dx = (vis.u - kidProj.x) * (width / Math.max(height, 1));
          const dy = vis.v - kidProj.y;
          const len = Math.hypot(dx, dy) || 1;
          const reach = Math.min(1, len / 0.3);
          const gx = (dx / len) * reach;
          const gy = (dy / len) * reach;
          chase.pose.gaze = {
            x: rest.x + (gx - rest.x) * chase.watchK,
            y: rest.y + (gy - rest.y) * chase.watchK,
          };
        } else {
          chase.pose.gaze = rest;
        }
      }
      const play = heroPose ?? idlePose ?? (chase ? chase.pose : null);
      const playK = 1 - Math.exp(-dt * (play ? play.rate : 4));
      for (const k of PLAY_OFFSET_KEYS) {
        const goal = play ? play[k] : 0;
        // Yaw eases the short way round, so full turns (pirouettes) never unwind
        playP[k] += (k === 'yaw' ? Math.atan2(Math.sin(goal - playP[k]), Math.cos(goal - playP[k])) : goal - playP[k]) * playK;
      }
      const headingTarget = play ? play.heading : 0;
      playHeading += Math.atan2(Math.sin(headingTarget - playHeading), Math.cos(headingTarget - playHeading)) * playK;
      if (play) playSpin = play.spin;
      if (!playEnabled || dragging) document.body.style.userSelect = heroPlay.holding || dragging ? 'none' : '';

      // Drag: he shrinks and follows the cursor (inside the hero), springs home + regrows on release
      const dragHome = { x: target.x + roam.value * heroW, y: target.y };
      let dragGoalX = 0;
      let dragGoalY = 0;
      if (dragging) {
        const v = viewHalf();
        const margin = droneRPx * 0.9;
        const top = Math.max(heroRect ? heroRect.top : 0, 0) + margin;
        const bottom = Math.max(top + 1, (heroRect ? heroRect.bottom : height) - margin);
        const cx = THREE.MathUtils.clamp(ptrPx.x, margin, width - margin);
        const cy = THREE.MathUtils.clamp(ptrPx.y, top, bottom);
        dragGoalX = ((cx / width) * 2 - 1) * v.w - dragHome.x;
        dragGoalY = -((cy / height) * 2 - 1) * v.h - dragHome.y;
      }
      const dragK = 1 - Math.exp(-dt * (dragging ? 14 : 4.5));
      dragOff.x += (dragGoalX - dragOff.x) * dragK;
      dragOff.y += (dragGoalY - dragOff.y) * dragK;
      dragScale += ((dragging ? DRAG_SCALE : 1) - dragScale) * (1 - Math.exp(-dt * (dragging ? 9 : 5)));

      // Movie mode: stage lights, letterbox, page dim, subtitles, camera shake
      const stage = party ? party.lights : 1;
      key.intensity = 2.4 * stage;
      fill.intensity = 0.8 * stage;
      ambient.intensity = 0.35 * (0.3 + 0.7 * stage);
      scene.environmentIntensity = 0.6 * (0.25 + 0.75 * stage);
      // Rim lights glow brighter in the dark — a silhouette with a bright edge
      rim.intensity = 3.2 * (1 + (1 - stage) * 0.8);
      rimLow.intensity = 2 * (1 + (1 - stage) * 0.8);
      opts.cinema.dark.set(1 - stage);
      opts.cinema.bars.set(party ? party.bars : 0);
      const shake = Math.max(party ? party.shake : 0, madP.shake);
      camera.position.set(Math.sin(t * 83) * 0.06 * shake, Math.sin(t * 71 + 1) * 0.05 * shake, 6.5);

      // Roam range keeps the whole drone on screen (extra room on the right for the menu tab)
      const roamRange = reduceMotion ? 0 : Math.max(0, Math.min(viewHalf().w - 1.05, 2.9));
      // In conversation (talking or waiting for your reply) he holds his spot so the bubbles can sit
      // beside him; he only roams once the conversation is over. Also holds still on hover.
      const talking = opts.chat.phase !== 'idle';
      const { target: roamTarget, phase: roamPhase } = roamer.update(
        t,
        roamRange,
        hovering || opts.bubbleLink.hovered,
        // The section spot is a fixed home — no roaming there (or while sneaking in)
        // Also heads home to the centre as soon as the hero starts scrolling away, so while the
        // showreel slides over (and shrinks) he stays hidden behind it instead of peeking out at the sides
        lastEmotion === 'sleepy' || talking || docked || chargeOn || dockAllowed || window.scrollY > height * 0.08 || madS.stage !== 'calm' || heroPlay.active || !!idlePose || dragging,
      );

      // Major moves wait a beat so the bubbles can pop away first
      if (Math.abs(roamTarget - acceptedRoam) > MAJOR_MOVE && modelReady && !reduceMotion) {
        if (roamPendingSince < 0) roamPendingSince = t;
        if (t - roamPendingSince >= PRE_MOVE_HIDE) {
          acceptedRoam = roamTarget;
          roamPendingSince = -1;
        }
      } else {
        acceptedRoam = roamTarget;
        roamPendingSince = -1;
      }

      if (opts.chat.pulse !== lastPulse) {
        lastPulse = opts.chat.pulse;
        bodyY.velocity += 1.3 * motionScale * driftScale;
        ledKick = 1;
      }
      ledKick *= Math.exp(-dt * 5);
      const talkTarget =
        opts.chat.phase === 'speaking' && !reduceMotion
          ? (0.5 + 0.5 * Math.sin(t * 19)) * (0.55 + 0.45 * Math.sin(t * 6.1))
          : 0;
      talk += (talkTarget - talk) * (1 - Math.exp(-dt * 30));

      const out = brain.update({
        t,
        dt,
        pointer,
        // During a session of idle antics he stays awake; afterwards he finally dozes off
        pointerMovedAt: idleAntics.holdAwake ? t : pointerMovedAt,
        pointerInWindow,
        pointerSpeed,
        hovering,
        scrollVelocity: scrollVelocity * (1 - dockW * 0.7),
        mode: opts.emotion,
        dragging,
        dragVelocity: NO_DRAG,
        playing:
          talking ? null : roamPhase === 'dash' || roamPhase === 'return' ? 'dash' : roamPhase === 'explore' ? 'explore' : null,
        playDir: Math.sign(roam.velocity) || 1,
        chat: { phase: opts.chat.phase, emotion: opts.chat.emotion },
      });
      if (party) {
        out.emotion = party.emotion;
        out.gaze = party.look;
        // Head follows the choreography, not the cursor, so the close-up faces you square on
        out.head = { yaw: party.look.x * 0.3, pitch: -party.look.y * 0.2, roll: 0 };
      }
      if (mad) {
        out.emotion = mad.emotion;
        if (mad.gaze) {
          out.gaze = mad.gaze;
          out.head = {
            yaw: mad.gaze.x * 0.6 * mad.headFollow,
            pitch: -mad.gaze.y * 0.4 * mad.headFollow,
            roll: out.head.roll,
          };
        }
      } else if (madOn && madS.stage !== 'calm') {
        // Reduced motion: the face still shows how he feels
        out.emotion =
          madS.stage === 'excited'
            ? 'happy'
            : madS.stage === 'psychic'
              ? 'surprised'
              : madS.stage === 'dizzy'
                ? 'sleepy'
                : 'angry';
      }
      if (play && !mad && !party) {
        if (play.emotion) out.emotion = play.emotion;
        if (play.gaze) {
          out.gaze = play.gaze;
          out.head = {
            yaw: play.gaze.x * 0.6 * play.headFollow,
            pitch: -play.gaze.y * 0.4 * play.headFollow,
            roll: out.head.roll,
          };
        }
      }
      // Docked in the corner: always turn eyes + head toward the cursor, measured from where he sits
      if (docked && !flight) {
        const v = viewHalf();
        const dx = (pointer.x - dockAnchor.x / v.w) * (width / Math.max(height, 1));
        const dy = pointer.y - dockAnchor.y / v.h;
        const len = Math.hypot(dx, dy) || 1;
        // Short reach: even a cursor right next to him (e.g. on the blue quick-picks at his left) gets a clear look
        const reach = Math.min(1, len / 0.2);
        const gx = (dx / len) * reach;
        const gy = (dy / len) * reach;
        out.gaze = { x: gx, y: gy };
        out.head = { yaw: gx * 0.7, pitch: -gy * 0.45, roll: out.head.roll };
        if (out.emotion === 'sleepy') out.emotion = 'neutral';
      }
      // data-drone-stare (0..1): turn eyes and head square to the camera, e.g. while flying up to the glass
      const stare = docked ? 0 : Number(anchorEl?.dataset.droneStare || 0);
      if (stare > 0) {
        const keep = 1 - Math.min(1, stare);
        out.gaze = { x: out.gaze.x * keep, y: out.gaze.y * keep };
        out.head = { yaw: out.head.yaw * keep, pitch: out.head.pitch * keep, roll: out.head.roll * keep };
      }
      // data-drone-look-w (0..1) + data-drone-look-x/y (-1..1, y up): face where he's flying, banking into it
      const lookW = docked ? 0 : Math.min(1, Number(anchorEl?.dataset.droneLookW || 0));
      if (lookW > 0) {
        const lx = Number(anchorEl?.dataset.droneLookX || 0);
        const ly = Number(anchorEl?.dataset.droneLookY || 0);
        const mix = (a: number, b2: number) => a + (b2 - a) * lookW;
        out.gaze = { x: mix(out.gaze.x, lx), y: mix(out.gaze.y, ly) };
        out.head = { yaw: mix(out.head.yaw, lx * 0.8), pitch: mix(out.head.pitch, -ly * 0.5), roll: mix(out.head.roll, -lx * 0.3) };
      }
      // Scripted face (services takeover): data-drone-emotion, -heart (heart eyes), -grin (big open grin)
      const anchorEmotion = docked ? undefined : anchorEl?.dataset.droneEmotion;
      if (anchorEmotion && anchorEmotion in EMOTION_PARAMS) out.emotion = anchorEmotion as typeof out.emotion;
      const anchorHeart = docked ? 0 : Math.min(1, Number(anchorEl?.dataset.droneHeart || 0));
      const anchorGrin = docked ? 0 : Math.min(1, Number(anchorEl?.dataset.droneGrin || 0));
      lastEmotion = out.emotion;

      // Face
      lerpParams(params, EMOTION_PARAMS[out.emotion], 1 - Math.exp(-dt * 7));

      // data-drone-blink: a new value makes him blink right now (scripted moments)
      const blinkTag = anchorEl?.dataset.droneBlink;
      if (blinkTag && blinkTag !== lastBlinkTag) {
        lastBlinkTag = blinkTag;
        blinkStart = t;
      }
      if (t > nextBlink && blinkStart < 0) blinkStart = t;
      let open = 1;
      if (blinkStart >= 0) {
        const phase = (t - blinkStart) / 0.2;
        if (phase >= 1) {
          blinkStart = -1;
          if (!doubleBlink && Math.random() < 0.25) {
            doubleBlink = true;
            nextBlink = t + 0.12;
          } else {
            doubleBlink = false;
            nextBlink = t + 2.2 + Math.random() * 4;
          }
        } else {
          open = 1 - Math.sin(phase * Math.PI) * 0.97;
        }
      }

      // Madness squint: eyes narrowed to slits
      open = Math.min(open, 1 - Math.max(madP.squint, playP.squint) * 0.68);
      // Spiral (dizzy / hypnotised) eyes never blink
      if (playP.spiral > 0.4) open = 1;

      // data-drone-wink (0..1): scripted wink of his left eye (services takeover)
      const anchorWink = docked ? 0 : Math.min(1, Number(anchorEl?.dataset.droneWink || 0));
      // No random blinks during the short or a scripted wink — they'd clip it
      if (party || anchorWink > 0.01) {
        open = 1;
        blinkStart = -1;
      }

      // Eyes lead, head follows
      const gk = 1 - Math.exp(-dt * 16);
      look.x += (out.gaze.x - look.x) * gk;
      look.y += (out.gaze.y - look.y) * gk;

      if (ctx) {
        drawPixelFace(ctx, {
          size: SCREEN_SIZE,
          grid: SCREEN_GRID,
          params,
          look,
          open,
          time: t,
          talk,
          wink: party ? party.wink : anchorWink,
          // Quest flights: out of the corner he talks with his face only (icon + scrolling word)
          glyph: quest.isActive() && !docked && !flight ? quest.faceGlyph : null,
          fx: {
            wide: playP.wide,
            spiral: playP.spiral,
            tight: playP.tight,
            spin: playSpin,
            scan: playP.scan,
            heart: anchorHeart,
            grin: anchorGrin,
          },
          charge: chargeOn
            ? {
                level: charge.level,
                impulse: charge.impulse,
                plugged: charge.isPlugged(t),
                time: t,
                wipe: charge.wipe(t),
              }
            : questChargeOn
              ? {
                  level: questCharge.level,
                  impulse: questCharge.impulse,
                  plugged: questCharge.isPlugged(t),
                  time: t,
                  wipe: questCharge.wipe(t),
                }
              : undefined,
        });
        // Asked "Why are you glitching?" → his face really does glitch, now and then, until the
        // quest is finished: a short burst of slice tears and a colour fringe, then calm again
        if (quest.isGlitching() && !reduceMotion) {
          if (t >= nextGlitch) {
            glitchUntil = t + 0.14 + Math.random() * 0.22;
            nextGlitch = t + 1.3 + Math.random() * 2.4;
          }
          if (t < glitchUntil) applyFaceGlitch(ctx, SCREEN_SIZE);
        }
        screenTexture.needsUpdate = true;
      }
      screen.scale.copy(screenBaseScale).multiplyScalar(ringSize * opts.screenScale);

      // LED ring breathes, flares when surprised, dims when sleepy
      const flare =
        (out.emotion === 'surprised' ? 0.8 : out.emotion === 'sleepy' ? -0.9 : out.emotion === 'angry' ? 0.4 : 0) +
        ledKick * 1.2 +
        talk * 0.5 +
        (chargeOn ? charge.impulse + charge.level * 0.4 : 0) +
        (questChargeOn ? questCharge.impulse + questCharge.level * 0.4 : 0) +
        (cheer ? cheer.led : 0) +
        (party ? party.led : 0) +
        madP.led +
        playP.led;
      ledMaterials.forEach((m) => {
        const target = 2.2 + flare + Math.sin(t * 1.4) * 0.25;
        const current = m.emissiveIntensity ?? target;
        m.emissiveIntensity = current + (target - current) * gk;
      });

      // Body: drift + hover bob + hop
      if (out.hop) bodyY.velocity += 2.6 * motionScale * driftScale;
      bobAmp += (out.bobAmp - bobAmp) * (1 - Math.exp(-dt * 2));
      bobPhase += dt * out.bobSpeed;
      const bob = Math.sin(bobPhase) * bobAmp * (reduceMotion ? 0 : 1);

      scrollSmooth += (scrollTarget - scrollSmooth) * (1 - Math.exp(-dt * 4));
      const s = scrollSmooth;

      // Extra spin (used by the fly-in) eases back to face the visitor
      spinYaw.update(0, dt);
      spinPitch.update(0, dt);

      // Roaming: spring toward the play target; skip-bounce while moving fast
      const roamX = roam.update(acceptedRoam, dt);
      const roamSpeed = Math.min(Math.abs(roam.velocity) / 2, 1);
      const skip = Math.abs(Math.sin(t * 9)) * 0.05 * roamSpeed;

      const drift = motionScale * DRIFT_SCALE * driftScale;

      // Flight between hero and dock: a jump up and over in an arc, twirling once,
      // curled into a ball, shrinking/growing on the way
      let baseX = target.x;
      let baseY = target.y;
      // Hand-over between spots that don't line up exactly (e.g. phones: earlier, smaller, off to the left):
      // keep him where he was and glide the gap closed instead of snapping into the new spot
      if (handoffPending) {
        handoffPending = false;
        if (!flight && lastBaseValid) {
          handoff.x = lastBaseX - target.x;
          handoff.y = lastBaseY - target.y;
        }
      }
      if (flight) {
        handoff.x = 0;
        handoff.y = 0;
      } else {
        const k = Math.exp(-dt * HANDOFF_RATE);
        handoff.x *= k;
        handoff.y *= k;
        baseX += handoff.x;
        baseY += handoff.y;
        if (width < 768 && !reduceMotion) {
          if (!follow.valid) {
            follow.x = baseX;
            follow.y = baseY;
            follow.valid = true;
          }
          // ~45ms time constant: irons out scroll stutter without visibly trailing the page
          const kf = 1 - Math.exp(-dt * FOLLOW_RATE);
          follow.x += (baseX - follow.x) * kf;
          follow.y += (baseY - follow.y) * kf;
          baseX = follow.x;
          baseY = follow.y;
        }
      }
      let flightYaw = 0;
      let flightPitch = 0;
      let flightRoll = 0;
      if (flight) {
        const p = Math.min(1, (t - flight.start) / FLIGHT_DUR);
        const e = flightEase(p);
        // Arc peaks early and is fully flattened well before touchdown, so the
        // last stretch is a slow horizontal glide into place
        const arc = Math.sin(Math.PI * Math.min(1, e / 0.85));
        const dirX = Math.sign(target.x - flight.from.x) || 1;
        baseX = THREE.MathUtils.lerp(flight.from.x, target.x, e);
        baseY = THREE.MathUtils.lerp(flight.from.y, target.y, e) + arc * FLIGHT_ARC;
        curScale = THREE.MathUtils.lerp(flight.from.scale, target.scale, e);
        // The twirl finishes early too, so he's already facing you while he glides in
        flightYaw = Math.min(1, e / 0.9) * Math.PI * 2 * dirX;
        flightRoll = -dirX * arc * 0.35;
        flightPitch = arc * 0.25 * (docked ? 1 : -1);
        // Let the body offsets fade with the flight instead of snapping to zero
        const fade = 1 - e;
        bodyX.value *= fade;
        bodyY.value *= fade;
        bodyX.velocity = 0;
        bodyY.velocity = 0;
        if (p >= 1) {
          flight = null; // no landing kick — he simply arrives
          brain.express('happy', 1.2, t);
          if (!docked && dockAllowed) setArrived(true);
          if (docked) opts.onDockLanded?.(true);
        }
      } else {
        curScale += (target.scale - curScale) * (1 - Math.exp(-dt * 6));
      }
      root.scale.setScalar(curScale * dragScale);
      // Keep the phone follow filter in step with flights, so it picks up exactly where he lands
      if (flight) {
        follow.x = baseX;
        follow.y = baseY;
        follow.valid = true;
      }
      lastBaseX = baseX;
      lastBaseY = baseY;
      lastBaseValid = true;

      // Full-charge short always plays dead centre in the viewport: glide there first, glide home after
      const pt = party ? t - charge.stateAt : 0;
      const centerW = party
        ? smoothstep01(pt / 0.5) * smoothstep01((PARTY_DUR - pt) / 0.6)
        : 0;
      const keepW = 1 - centerW;
      baseX *= keepW;
      baseY *= keepW;

      // Springs only carry offsets, so the drone sticks to the hero while it scrolls
      root.position.set(
        baseX +
          bodyX.update(out.body.x * drift, dt) * keepW +
          roamX * heroW * keepW +
          (party ? party.x : 0) +
          madP.x +
          playP.x +
          dragOff.x +
          (cheer ? cheer.x : 0) +
          Math.sin(t * 70) * 0.015 * (charge.impulse + questCharge.impulse), // buzz on each charge impulse
        baseY +
          (bodyY.update(out.body.y * drift, dt) + (bob * DRIFT_SCALE + skip) * driftScale + s * 0.35 * heroW) * keepW -
          scrollSigned * 0.35 * curScale * dockW +
          (party ? party.y : 0) +
          (cheer ? cheer.y : 0) +
          madP.y +
          playP.y +
          dragOff.y,
        bodyZ.update(out.body.z * drift, dt) + (party ? party.z : 0) + (cheer ? cheer.z : 0) + madP.z + playP.z,
      );

      // Crack the glass exactly where he is this frame (after his final position is set), in
      // viewport pixels — the canvas can be offset/taller than the viewport on phones
      if (crackNow) {
        const rect = container.getBoundingClientRect();
        // Only when he actually flies into the glass — never a crack out of nowhere (e.g. while he's
        // docked or busy with the quest)
        if (mad) {
          root.updateMatrixWorld();
          root.getWorldPosition(madProj).project(camera);
          madness.impact(rect.left + ((madProj.x + 1) / 2) * rect.width, rect.top + ((1 - madProj.y) / 2) * rect.height);
        }
      }

      // Drag kids: fly in from the sides, trail behind him in a wobbly line, scatter on release
      {
        const v = viewHalf();
        // Same size as the dragged Pixel
        const kidScale = curScale * DRAG_SCALE;
        const spacing = MODEL_RADIUS * kidScale * 2.2;
        const hTop = Math.max(heroRect ? heroRect.top : 0, 0);
        const hBottom = heroRect ? heroRect.bottom : height;
        const toWy = (py: number) => -((py / height) * 2 - 1) * v.h;
        const rW = MODEL_RADIUS * kidScale;
        swarm.update(
          t,
          dt,
          { x: root.position.x, y: root.position.y },
          spacing,
          v,
          {
            minX: -v.w + rW,
            maxX: v.w - rW,
            minY: Math.min(toWy(hBottom) + rW, toWy(hTop) - rW),
            maxY: toWy(hTop) - rW,
          },
        );
        for (let i = 0; i < kidRoots.length; i++) {
          const g = kidRoots[i];
          const kid = swarm.kids[i];
          if (!kid) {
            g.visible = false;
            continue;
          }
          g.visible = true;
          const wob = Math.sin(t * 3 + kid.seed);
          g.position.set(kid.x, kid.y + Math.sin(t * 2.4 + kid.seed * 2) * 0.03, kid.z);
          const pop = kid.pop * kid.pop * (3 - 2 * kid.pop);
          const sc = kidScale * Math.max(pop, 0.001);
          // Squash on bounces and bumps (wider, flatter for a beat)
          const sq = kid.squash * 0.28;
          g.scale.set(sc * (1 + sq), sc * (1 - sq), sc * (1 + sq * 0.5));
          // Turn toward where they're going, bank into turns, plus their own tricks (rolls, flips)
          g.rotation.set(
            THREE.MathUtils.clamp(-kid.vy * 0.14, -0.5, 0.5) + wob * 0.06 + kid.flip,
            THREE.MathUtils.clamp(kid.vx * 0.2, -0.8, 0.8) + Math.sin(t * 0.9 + kid.seed) * 0.15,
            THREE.MathUtils.clamp(-kid.vx * 0.1, -0.4, 0.4) + wob * 0.08 + kid.roll,
            'YXZ',
          );
        }
      }

      // Footer chase visitors: far behind Pixel, small and a touch hazy with distance
      for (let i = 0; i < visitorRoots.length; i++) {
        const g = visitorRoots[i];
        const s = chase ? chase.visitors[i] : null;
        if (!s || s.pop <= 0.001) {
          g.visible = false;
          continue;
        }
        const v = viewHalf();
        const z = CHASE_DEPTH + s.dz;
        // u/v are fractions of the visible half-size at that depth
        const f = (camera.position.z - z) / camera.position.z;
        g.visible = true;
        g.position.set(s.u * v.w * f, s.v * v.h * f, z);
        const pop = s.pop * s.pop * (3 - 2 * s.pop);
        const sc = curScale * 0.5 * Math.max(pop, 0.001);
        const sq = s.squash * 0.28;
        g.scale.set(sc * (1 + sq), sc * (1 - sq), sc * (1 + sq * 0.5));
        g.rotation.set(s.pitch, s.yaw * 1.15, s.roll, 'YXZ');
        // Their own faces: the chaser furious, the runner freaked out (wide eyes, worried brows, gasping)
        const face = visitorFaces[i];
        if (face?.ctx) {
          drawPixelFace(face.ctx, {
            size: SCREEN_SIZE,
            grid: SCREEN_GRID,
            params: s.mood === 'angry' ? ANGRY_FACE : SCARED_FACE,
            look: s.look,
            open: 1,
            time: t,
            talk: s.mood === 'scared' ? 0.55 + Math.sin(t * 22) * 0.25 : 0,
          });
          face.tex.needsUpdate = true;
        }
      }

      // Hypnotised or dragged: shove hero UI out of the way on contact (everything glides home afterwards)
      const pushers: Array<{ x: number; y: number; r: number }> = [{ x: dronePx.x, y: dronePx.y, r: droneRPx * 0.9 }];
      if (dragging) {
        for (let i = 0; i < swarm.kids.length && i < kidRoots.length; i++) {
          if (swarm.kids[i].out) continue;
          kidProj.copy(kidRoots[i].position).project(camera);
          pushers.push({ x: ((kidProj.x + 1) / 2) * width, y: ((1 - kidProj.y) / 2) * height, r: droneRPx * 0.85 });
        }
      }
      updateUiPush(t, dt, pushers, (!!play && play.push) || dragging);

      // Chat bubbles: shown only while settled, attached to the drone's upper-right
      // Hero only — the chat never shows in the docked corner
      const moving =
        !!flight ||
        wantDock ||
        docked ||
        dockSwitchAt >= 0 ||
        roamPendingSince >= 0 ||
        !modelReady ||
        t < introUntil + 0.2 ||
        Math.abs(roam.value - acceptedRoam) > 0.15 ||
        chargeOn ||
        heroPlay.active ||
        madS.stage === 'psychic';
      if (moving) settledSince = -1;
      else if (settledSince < 0) settledSince = t;
      const wantBubbles = settledSince >= 0 && t - settledSince >= SETTLE_SHOW;
      if (wantBubbles !== bubblesVisible) {
        bubblesVisible = wantBubbles;
        opts.onBubblesVisible?.(bubblesVisible);
      }
      // Follows the resting spot (not the bob) so the bubbles float steadily
      bubbleWorld
        .set(baseX + roamX * heroW + 0.72 * curScale, baseY + s * 0.35 * heroW + 0.46 * curScale, 0)
        .project(camera);
      const bx = ((bubbleWorld.x + 1) / 2) * width;
      const by = ((1 - bubbleWorld.y) / 2) * height;
      const bk = bubblesVisible ? 1 - Math.exp(-dt * 14) : 1;
      bubbleX += (bx - bubbleX) * bk;
      bubbleY += (by - bubbleY) * bk;
      const panelW = Math.min(460, width - 24);
      opts.bubbleLink.x.set(THREE.MathUtils.clamp(bubbleX, 12, width - 12 - panelW));
      opts.bubbleLink.y.set(bubbleY);


      // Bank into the direction of travel and turn toward where it's going
      const lean = THREE.MathUtils.clamp(-roam.velocity * 0.1, -0.3, 0.3) * heroW;
      const heading = THREE.MathUtils.clamp(roam.velocity * 0.12, -0.35, 0.35) * heroW;

      // Head: look direction + tilt, plus scroll (hero: progress, dock: wobble) and drag rotation
      root.rotation.set(
        pitch.update(out.head.pitch * motionScale, dt) -
          s * 0.3 * heroW +
          spinPitch.value +
          flightPitch +
          scrollSigned * 1.1 * dockW +
          (party ? party.pitch : 0) +
          (cheer ? cheer.pitch : 0) +
          madP.pitch +
          playP.pitch +
          // Flat on the "table": tipped forward so you see him from above
          playP.board * (Math.PI / 2),
        yaw.update(out.head.yaw * motionScale, dt) +
          s * 0.6 * heroW +
          spinYaw.value +
          heading +
          flightYaw +
          (party ? party.yaw : 0) +
          (cheer ? cheer.yaw : 0) +
          madP.yaw +
          playP.yaw,
        roll.update(out.head.roll * motionScale, dt) +
          Math.sin(t * 0.6) * 0.02 * motionScale +
          lean +
          flightRoll +
          scrollSigned * 0.3 * dockW +
          (party ? party.roll : 0) +
          (cheer ? cheer.roll : 0) +
          madP.roll +
          playP.roll,
        'YXZ',
      );
      // On the "table" he steers around the view axis, front pointing where he's going
      const boardTurn = playHeading * Math.min(1, playP.board * 1.5);
      if (Math.abs(boardTurn) > 1e-4) root.quaternion.premultiply(headingQ.setFromAxisAngle(Z_AXIS, boardTurn));

      // Faceplate is always on top, so fade it out as the face turns away from the camera
      if (faceGroup) {
        root.updateMatrixWorld();
        faceGroup.getWorldPosition(faceWorldPos);
        faceGroup.getWorldQuaternion(faceWorldQuat);
        faceNormalWorld.copy(Z_AXIS).applyQuaternion(faceWorldQuat);
        const facing = faceNormalWorld.dot(camera.position.clone().sub(faceWorldPos).normalize());
        const alpha = THREE.MathUtils.smoothstep(facing, 0.05, 0.3);
        plateMaterial.opacity = alpha;
        bezelMaterial.opacity = alpha;
        screenMaterial.opacity = alpha;
        faceGroup.visible = alpha > 0.001;
      }

      screen.getWorldPosition(glowWorld);
      redGlow.position.copy(glowWorld).add(glowOffset);

      mixer?.update(dt);

      // Side plates in/out (after the mixer so built-in clips can't override it)
      const armsTarget = party
        ? party.arms
        : cheer
          ? cheer.arms
          : mad && mad.arms !== null
          ? mad.arms
          : play && play.arms !== null
          ? play.arms
          : flight || t < introUntil
          ? 1
          : opts.armsMode === 'open' ? 0 : opts.armsMode === 'closed' ? 1 : out.armsClosed;
      const armsT = THREE.MathUtils.clamp(arms.update(armsTarget, dt), -0.08, 1.04);
      if (Math.abs(armsT - lastArmsT) > 1e-4) {
        applyArms(armParts, armsT);
        lastArmsT = armsT;
      }

      // Chat float: track the actual point on Pixel's upper-right where the bubble's tail attaches —
      // through his full transform, so his bob, drift, lean, tilt and head turns all carry the chat —
      // compared with where that point sits when he rests at home. Followed with a soft lag, plus a
      // slow hover of its own so the bubbles never sit dead still.
      root.updateMatrixWorld();
      camera.updateMatrixWorld();
      floatNow.copy(CHAT_ATTACH).applyMatrix4(root.matrixWorld).project(camera);
      floatHome
        .set(heroAnchor.x + CHAT_ATTACH.x * curScale, heroAnchor.y + CHAT_ATTACH.y * curScale, 0)
        .project(camera);
      const hoverAmt = reduceMotion ? 0 : 1;
      // Follows ~14% of Pixel's motion, with a faint own hover
      const FOLLOW = 0.14;
      const fdx = ((floatNow.x - floatHome.x) / 2) * width * heroW * FOLLOW + Math.sin(t * 0.8) * 0.7 * hoverAmt;
      const fdy = (-(floatNow.y - floatHome.y) / 2) * height * heroW * FOLLOW + Math.sin(t * 1.15 + 0.6) * 0.8 * hoverAmt;
      // Critically damped spring: eases in and out of every shift, never snaps or overshoots
      const OMEGA = 3.2;
      const step = Math.min(dt, 1 / 30);
      floatVX += ((fdx - floatX) * OMEGA * OMEGA - 2 * OMEGA * floatVX) * step;
      floatVY += ((fdy - floatY) * OMEGA * OMEGA - 2 * OMEGA * floatVY) * step;
      floatX += floatVX * step;
      floatY += floatVY * step;
      opts.bubbleLink.dx.set(floatX);
      opts.bubbleLink.dy.set(floatY);

      renderer.render(scene, camera);
    };
    tick();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      document.body.style.cursor = '';
      window.removeEventListener('scroll', readScroll);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      document.body.style.userSelect = '';
      heroPlay.reset();
      swarm.reset();
      footerChase.reset();
      visitorFaces.forEach((f) => f.tex.dispose());
      idleAntics.detach();
      resetUiPush();
      window.removeEventListener('blur', onLeave);
      window.removeEventListener('resize', onResize);
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.geometry.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((m) => m.dispose());
        }
      });
      screenTexture.dispose();
      environment.dispose();
      draco.dispose();
      renderer.dispose();
      setInteractionLock(false);
      renderer.domElement.remove();
    };
  }, [containerRef, options.modelUrl, options.faceSide]);

  return { status, progress, armsFound };
}
